import { useState, useEffect, useCallback } from 'react'
import { Api, Captures, CaptureLabelKind, UserType } from '../api'
import type { CaptureLabelSchema } from '../api'
import { errorSentence } from '../lib/errorSentence'
import { groupByCategory } from '../lib/labelCategories'
import AppHeader from '../components/AppHeader'

interface Props {
  onNavigate: (hash: string) => void
  onLogout: () => void
}

export default function SettingsPage({ onNavigate, onLogout }: Props) {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null)

  useEffect(() => {
    Api.getCurrentUser().then(r => setIsAdmin(r.user.type === UserType.Admin)).catch(() => setIsAdmin(false))
  }, [])

  return (
    <div className="min-h-screen bg-stone-50">
      <AppHeader active="settings" onNavigate={onNavigate} onLogout={onLogout} />

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {isAdmin === false && (
          <div className="text-sm text-stone-600 bg-white border border-stone-200 rounded px-3 py-2">Settings are for admins only.</div>
        )}
        {isAdmin && (
          <>
            <LabelsAdmin kind={CaptureLabelKind.Catch} />
            <LabelsAdmin kind={CaptureLabelKind.Length} />
            <LabelsAdmin kind={CaptureLabelKind.Weight} />
          </>
        )}
      </main>
    </div>
  )
}

function LabelsAdmin({ kind }: { kind: CaptureLabelKind }) {
  const [labels, setLabels] = useState<CaptureLabelSchema[]>([])
  const [category, setCategory] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [busyId, setBusyId] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    Captures.listCaptureLabels({ body: {} })
      .then(r => setLabels(r.labels.filter(l => l.kind === kind)))
      .catch(e => setError(String(e)))
  }, [kind])
  useEffect(() => { load() }, [load])

  const categoriesId = `label-categories-${kind}`
  const categories = Array.from(new Set(labels.map(l => l.category).filter(Boolean))).sort()

  async function add(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await Captures.createCaptureLabel({
        createCaptureLabelRequest: { kind, category, name, description: description || null },
      })
      // Keep the category: labels tend to be added a category at a time.
      setName(''); setDescription('')
      load()
    } catch (e) {
      setError(await errorSentence(e, 'Could not add the label — try again.'))
    }
  }

  async function save(label: CaptureLabelSchema, newCategory: string, newName: string, newDescription: string) {
    setBusyId(label.id)
    setError(null)
    try {
      await Captures.updateCaptureLabel({
        updateCaptureLabelRequest: { labelId: label.id, category: newCategory, name: newName, description: newDescription || null },
      })
      setEditingId(null)
      load()
    } catch (e) {
      setError(await errorSentence(e, 'Could not save the label — try again.'))
    } finally {
      setBusyId(null)
    }
  }

  async function remove(label: CaptureLabelSchema) {
    if (!window.confirm(`Delete "${label.name}"? It comes off every capture that has it.`)) return
    setBusyId(label.id)
    try {
      await Captures.deleteCaptureLabel({ deleteCaptureLabelRequest: { labelId: label.id } })
      load()
    } catch (e) {
      setError(await errorSentence(e, 'Could not delete the label — try again.'))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <section className="bg-white border border-stone-200 rounded-lg p-4 space-y-3">
      <div>
        <h2 className="font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500">{kind} labels</h2>
        <p className="text-xs text-stone-500 mt-1">
          {kind === CaptureLabelKind.Catch
            ? "Shown in the capture panel's main view, under Triage, where reviewers switch them on or off per capture."
            : `Shown in the capture panel's ${kind} debug view, where reviewers switch them on or off per capture.`}
        </p>
      </div>
      {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">{error}</div>}
      <form onSubmit={add} className="flex flex-wrap gap-2 items-end text-sm">
        <Field label="Category">
          <input value={category} onChange={e => setCategory(e.target.value)} placeholder="optional" list={categoriesId}
            className="border border-stone-300 rounded px-2 py-1 text-sm w-36" />
        </Field>
        <Field label="Name">
          <input value={name} onChange={e => setName(e.target.value)} required
            className="border border-stone-300 rounded px-2 py-1 text-sm" />
        </Field>
        <Field label="Description">
          <input value={description} onChange={e => setDescription(e.target.value)} placeholder="optional"
            className="border border-stone-300 rounded px-2 py-1 text-sm w-72" />
        </Field>
        <button type="submit" className="font-mono text-[10px] tracking-[0.15em] uppercase bg-bear text-white px-3 py-1.5 rounded hover:bg-bear-light">
          Add
        </button>
      </form>
      <datalist id={categoriesId}>
        {categories.map(c => <option key={c} value={c} />)}
      </datalist>
      <div className="space-y-3">
        {groupByCategory(labels).map(([cat, group]) => (
          <div key={cat}>
            <h3 className="font-mono text-[10px] tracking-[0.15em] uppercase text-stone-400 mb-1">{cat || 'Uncategorized'}</h3>
        {group.map(l => editingId === l.id ? (
          <LabelEditRow key={l.id} label={l} busy={busyId === l.id} categoriesId={categoriesId}
            onSave={(c, n, d) => save(l, c, n, d)} onCancel={() => setEditingId(null)} />
        ) : (
          <div key={l.id} className="flex items-center gap-3 text-sm py-1 border-t border-stone-100 first:border-t-0">
            <span className="flex-1">{l.name} {l.description && <span className="text-stone-400 text-xs">{l.description}</span>}</span>
            <button disabled={busyId === l.id} onClick={() => setEditingId(l.id)}
              className="font-mono text-[10px] uppercase text-stone-500 hover:text-bear">
              Edit
            </button>
            <button disabled={busyId === l.id} onClick={() => remove(l)}
              className="font-mono text-[10px] uppercase text-stone-500 hover:text-rose-700">
              Delete
            </button>
          </div>
        ))}
          </div>
        ))}
        {labels.length === 0 && <div className="text-sm text-stone-500">No {kind} labels yet.</div>}
      </div>
    </section>
  )
}

/** Inline category/name/description editor. Changes keep the label on its captures. */
function LabelEditRow({ label, busy, categoriesId, onSave, onCancel }: {
  label: CaptureLabelSchema
  busy: boolean
  categoriesId: string
  onSave: (category: string, name: string, description: string) => void
  onCancel: () => void
}) {
  const [category, setCategory] = useState(label.category)
  const [name, setName] = useState(label.name)
  const [description, setDescription] = useState(label.description ?? '')
  return (
    <form onSubmit={e => { e.preventDefault(); onSave(category, name, description) }}
      onKeyDown={e => { if (e.key === 'Escape') onCancel() }}
      className="flex flex-wrap items-center gap-2 text-sm py-1 border-t border-stone-100 first:border-t-0">
      <input value={category} onChange={e => setCategory(e.target.value)} placeholder="category" list={categoriesId} aria-label="Category"
        className="border border-stone-300 rounded px-2 py-1 text-sm w-36" />
      <input value={name} onChange={e => setName(e.target.value)} required autoFocus aria-label="Name"
        className="border border-stone-300 rounded px-2 py-1 text-sm" />
      <input value={description} onChange={e => setDescription(e.target.value)} placeholder="optional" aria-label="Description"
        className="border border-stone-300 rounded px-2 py-1 text-sm flex-1 min-w-48" />
      <button type="submit" disabled={busy}
        className="font-mono text-[10px] tracking-[0.15em] uppercase bg-bear text-white px-3 py-1.5 rounded hover:bg-bear-light">
        Save
      </button>
      <button type="button" onClick={onCancel}
        className="font-mono text-[10px] uppercase text-stone-500 hover:text-bear">
        Cancel
      </button>
    </form>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500">{label}</span>
      {children}
    </label>
  )
}

